import { FlexColumn, FlexRow, Loading, useLoading } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { HeadingSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useUpdateEffect } from '../../../hooks/useCustomEffects';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { createJobActivity, getJobActivity, updateJobActivity } from '../../../services/jobTypesService';
import {
  editActivityErrorState,
  editActivityState,
  editActivityTouchedState,
  jobActivityDetailState,
  selectedJobActivityIdState,
  updateJobActivitiesState,
} from '../../../storage/jobTypesStates';
import { FormActionsRow } from '../../shared/FormActionsRow';
import { JobActivityFields } from './JobActivityFields';

export function JobActivityDetail() {
  const { theme, css } = useStyles();
  const { jobTypeId } = useParams();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const { loading: requesting, startLoading: startRequesting, stopLoading: stopRequesting } = useLoading();
  const [jobActivity, setJobActivity] = useRecoilState(jobActivityDetailState);
  const [selectedActivityId, setSelectedActivityId] = useRecoilState(selectedJobActivityIdState);
  const resetSelectedActivityId = useResetRecoilState(selectedJobActivityIdState);
  const [editActivity, setEditActivity] = useRecoilState(editActivityState);
  const resetActivityTouchedState = useResetRecoilState(editActivityTouchedState);
  const resetEditActivity = useResetRecoilState(editActivityState);
  const editActivityHasError = useRecoilValue(editActivityErrorState);
  const canSubmit = !editActivityHasError;
  const setUpdateActivities = useSetRecoilState(updateJobActivitiesState);
  const [updateActivity, setUpdateActivity] = useState<string>();
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const editMode = !(selectedActivityId === 'new');

  useEffect(() => {
    return () => {
      setSelectedActivityId(undefined);
      setJobActivity(undefined);
    };
  }, []);

  useUpdateEffect(() => {
    if (updateActivity) {
      startLoading();
      loadActivityDetail();
      setUpdateActivity(undefined);
      stopLoading();
    }
  }, [updateActivity]);

  useEffect(() => {
    if (!editMode) {
      setJobActivity(undefined);
      resetActivityTouchedState();
      resetEditActivity();
      stopLoading();
      return;
    }
    if (!selectedActivityId || !jobTypeId) {
      stopLoading();
      return;
    }
    startLoading();
    loadActivityDetail();
    stopLoading();
  }, [selectedActivityId, editMode]);

  const loadActivityDetail = async () => {
    if (!selectedActivityId || !jobTypeId) return;
    const { activity, error } = await getJobActivity(jobTypeId, selectedActivityId);
    if (!error && activity) {
      setJobActivity(activity);
      setEditActivity((prev) => ({
        ...prev,
        ...activity,
        forms: activity.forms.map((f) => f._id),
        nextActivities: activity.nextActivities.map((a) => a._id),
      }));
    } else {
      setSelectedActivityId(undefined);
      showFailFeedback(error || 'No se pudo cargar la actividad');
    }
  };

  if (!selectedActivityId) return null;

  const onSubmit = async () => {
    if (!jobTypeId) return;
    const onSuccess = (successMessage: string) => {
      showPositiveFeedback(successMessage);
      setUpdateActivities('update');
    };
    startRequesting();
    if (editMode) {
      const { error } = await updateJobActivity(jobTypeId, selectedActivityId, editActivity);
      if (!error) {
        onSuccess(FEEDBACK.edited(FEEDBACK_PREFIXES.jobActivity, editActivity.name));
        setUpdateActivity('update');
      } else {
        showFailFeedback(error);
      }
    } else {
      const { id, error } = await createJobActivity(jobTypeId, editActivity);
      if (!error) {
        onSuccess(FEEDBACK.created(FEEDBACK_PREFIXES.jobActivity));
        setSelectedActivityId(id);
      } else {
        showFailFeedback(error);
      }
    }
    stopRequesting();
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <FlexColumn
      classNames={css({
        flex: 1,
        overflow: 'hidden',
        [theme.mediaQuery.extraLarge]: {
          maxWidth: '60%',
          minWidth: '700px',
        },
      })}
    >
      <FlexRow
        classNames={css({
          alignItems: 'center',
          justifyContent: 'space-between',
        })}
      >
        <HeadingSmall margin={0}>{jobActivity?.name}</HeadingSmall>
        {editMode && (
          <Checkbox
            checked={editActivity.active}
            onChange={(e) =>
              setEditActivity((prev) => ({
                ...prev,
                active: e.target.checked,
              }))
            }
          >
            Activo
          </Checkbox>
        )}
      </FlexRow>
      <FlexColumn classNames={css({ flex: 1, overflow: 'auto' })}>
        <JobActivityFields />
      </FlexColumn>
      <FormActionsRow
        canSubmit={canSubmit}
        loading={requesting}
        onSubmit={onSubmit}
        editMode={editMode}
        onCancel={resetSelectedActivityId}
      />
    </FlexColumn>
  );
}
